import type { CartValidationErrorResponse } from '@libs/common-node';
import type { CartNotification } from '@libs/common-types';
import {
    isCartNotificationCouponEffect,
    isCartNotificationDiscontinuedItemsRemoved,
    isCartNotificationSddInvalidDeliveryWindow,
    isCartNotificationTreatPointsRemoved,
} from '@libs/common-types';
import { CartNotificationAction } from '@libs/core';

import type { CartValidationErrorDetails } from '../types';
import { CartValidationErrorKey } from './notificationMaps';

const getCartNotificationDedupeKey = (notification: CartNotification): string => {
    if (isCartNotificationCouponEffect(notification)) {
        return [notification.action, notification.couponCode.toUpperCase(), notification.code, notification.type].join(
            '|'
        );
    }

    if (isCartNotificationSddInvalidDeliveryWindow(notification)) {
        return [notification.action, notification.errorCode ?? '', notification.message ?? ''].join('|');
    }

    if (isCartNotificationDiscontinuedItemsRemoved(notification)) {
        return [notification.action, ...notification.productNames].join('|');
    }

    if (isCartNotificationTreatPointsRemoved(notification)) {
        return notification.action;
    }

    const baseKey = [notification.action, notification.lineItemId, notification.sku];

    switch (notification.action) {
        case CartNotificationAction.LINE_ITEM_QTY_UPDATE:
            return [...baseKey, notification.maxOrderQty].join('|');
        case CartNotificationAction.LINE_ITEM_AUTOSHIP_REMOVED:
            return [...baseKey, notification.productName].join('|');
        case CartNotificationAction.SCHEDULED_DELIVERY_DATE_UPDATED:
            return [...baseKey, notification.scheduledDeliveryDate ?? ''].join('|');
        default:
            return baseKey.join('|');
    }
};

export function mergeValidationNotificationsForSingleUpdateCycle(
    initialNotifications?: CartNotification[] | null,
    validatedNotifications?: CartNotification[] | null
): CartNotification[] {
    const seenNotificationKeys = new Set<string>();

    const initialWithoutCouponEffects = (initialNotifications ?? []).filter(
        notification => !isCartNotificationCouponEffect(notification)
    );

    return [...initialWithoutCouponEffects, ...(validatedNotifications ?? [])].filter(notification => {
        const notificationKey = getCartNotificationDedupeKey(notification);

        if (seenNotificationKeys.has(notificationKey)) {
            return false;
        }

        seenNotificationKeys.add(notificationKey);
        return true;
    });
}

export function getNotificationsFromCartValidationResponse(details: CartValidationErrorResponse): CartNotification[] {
    return [...(details.notifications ?? []), ...(details.errors?.notifications ?? [])];
}

export function hasValidationErrorCode(
    errors: CartValidationErrorDetails[],
    errorCode: CartValidationErrorKey
): boolean {
    return errors.some(error => error.errorCode === errorCode);
}

const isLoyaltyDownTreatPointsRemovedNotification = (notification: CartNotification): boolean =>
    isCartNotificationTreatPointsRemoved(notification) &&
    notification.errorCode === CartValidationErrorKey.LOYALTY_DOWN;

const getCartValidationErrorDetails = (error: unknown): CartValidationErrorResponse | null => {
    if (!error || typeof error !== 'object' || !('errorDetails' in error)) {
        return null;
    }

    const { errorDetails } = error as { errorDetails: unknown };
    if (!errorDetails || typeof errorDetails !== 'object') {
        return null;
    }

    return errorDetails as CartValidationErrorResponse;
};

export function hasLoyaltyDownNotificationInCartValidationError(error: unknown): boolean {
    const details = getCartValidationErrorDetails(error);
    if (!details) {
        return false;
    }

    return getNotificationsFromCartValidationResponse(details).some(isLoyaltyDownTreatPointsRemovedNotification);
}

export function hasLoyaltyDownCartValidationError(
    parsedErrors: CartValidationErrorDetails[] | null | undefined,
    error?: unknown
): boolean {
    return (
        hasValidationErrorCode(parsedErrors ?? [], CartValidationErrorKey.LOYALTY_DOWN) ||
        hasLoyaltyDownNotificationInCartValidationError(error)
    );
}

/**
 * Codes that must not keep the cart invalid once the Auth0 loyalty-down banner is staged.
 * `UNEXPECTED_ERROR_BANNER` is included because `parseErrorResponse` falls back to it when
 * loyalty-down arrives only as a TreatPointsRemoved notification with no cart/line errors.
 */
export const LOYALTY_DOWN_NON_BLOCKING_CART_ERROR_CODES = [
    CartValidationErrorKey.LOYALTY_DOWN,
    CartValidationErrorKey.TREAT_POINTS_REMOVED,
    CartValidationErrorKey.UNEXPECTED_ERROR_BANNER,
] as const;

export function clearLoyaltyDownBlockingCartErrors(removeErrorByCode?: (errorCode: string) => void): void {
    if (!removeErrorByCode) {
        return;
    }

    for (const errorCode of LOYALTY_DOWN_NON_BLOCKING_CART_ERROR_CODES) {
        removeErrorByCode(errorCode);
    }
}

// True when a BFF banner overrides a scheduled-delivery-date change (mixed-cart or autoship start-date error pairs).
export function checkHasBannerOverridingSchedule(errors: CartValidationErrorDetails[]): boolean {
    const hasMixedCartBanner = hasValidationErrorCode(errors, CartValidationErrorKey.MIXED_CART_BANNER);
    const hasInvalidScheduledDateError = hasValidationErrorCode(
        errors,
        CartValidationErrorKey.AUTOSHIP_SCHEDULED_DATE_INVALID_FULFILLMENT_METHOD
    );
    const hasAutoshipStartDateChanged = hasValidationErrorCode(
        errors,
        CartValidationErrorKey.AUTOSHIP_START_DATE_CHANGED
    );
    const hasFutureStartDateError = hasValidationErrorCode(
        errors,
        CartValidationErrorKey.AUTOSHIP_FUTURE_START_DATE_ERROR
    );

    return (
        (hasMixedCartBanner && hasInvalidScheduledDateError) || (hasAutoshipStartDateChanged && hasFutureStartDateError)
    );
}

export function isScheduledDeliveryDateUpdatedNotification(notification: CartNotification): boolean {
    return 'action' in notification && notification.action === CartNotificationAction.SCHEDULED_DELIVERY_DATE_UPDATED;
}

export function filterScheduledDeliveryNotificationsForValidationErrors(
    parsedErrors: CartValidationErrorDetails[],
    notifications: CartNotification[]
): CartNotification[] {
    return checkHasBannerOverridingSchedule(parsedErrors)
        ? notifications.filter(notification => !isScheduledDeliveryDateUpdatedNotification(notification))
        : notifications;
}
